/**
 * Where the reader is in the Wealth section: the tab that was open, the month
 * under review, and the transaction list's search, category, account and sort.
 *
 * None of it can live in a component. Each Wealth tab is its own route, and a
 * route change builds the section's layout and provider again
 * (`withLayout`, src/client/root.tsx), so anything held in their state is the
 * current month and no filter again after every click on a tab, and the top
 * menu's Wealth link always opens Overview. Someone reviewing January 2020
 * who looked at a chart and came back was on this month's Overview.
 *
 * Kept for the browser session (`sessionStorage`): it survives moving around
 * the app and a reload, and a newly opened app starts on this month. It is a
 * position on this screen, not a setting, so it is not synced to the server.
 * Paper keeps its own, as it does of every Wealth key that is not layout: the
 * search holds what the reader typed, which may be a real payee's name, and a
 * demonstration must not open on it.
 *
 * Imports only the mode's names, so the router and the check suites can read it.
 */
import { ACCOUNT_MODE_KEY, paperStorageKey } from "./accountMode";

export const WEALTH_VIEW_KEY = "mpp:wealth:view:v1";

/** This device's key: the paper twin while it shows the paper account. */
function viewKey(): string {
  try {
    if (typeof localStorage !== "undefined" && localStorage.getItem(ACCOUNT_MODE_KEY) === "paper") return paperStorageKey(WEALTH_VIEW_KEY);
  } catch {
    /* no storage: Real */
  }
  return WEALTH_VIEW_KEY;
}

export type WealthTxSort = { key: "date" | "amount"; dir: "asc" | "desc" };

export type WealthView = {
  /** The Wealth address last open, with its query (`/wealth/spending?view=transactions`). */
  href: string | null;
  /** `YYYY-MM`. */
  month: string | null;
  q: string;
  category: string;
  accountId: number | null;
  sort: WealthTxSort | null;
};

const EMPTY: WealthView = { href: null, month: null, q: "", category: "", accountId: null, sort: null };

/** What was stored, with anything that is not the shape above dropped. */
export function parseWealthView(raw: string | null): WealthView {
  if (!raw) return { ...EMPTY };
  let v: Record<string, unknown>;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return { ...EMPTY };
    v = parsed as Record<string, unknown>;
  } catch {
    return { ...EMPTY };
  }
  const sort = v.sort as { key?: unknown; dir?: unknown } | null | undefined;
  return {
    href: typeof v.href === "string" && inWealth(v.href.split(/[?#]/)[0]!) ? v.href : null,
    month: typeof v.month === "string" && /^\d{4}-(0[1-9]|1[0-2])$/.test(v.month) ? v.month : null,
    q: typeof v.q === "string" ? v.q.slice(0, 200) : "",
    category: typeof v.category === "string" ? v.category.slice(0, 80) : "",
    accountId: typeof v.accountId === "number" && Number.isInteger(v.accountId) ? v.accountId : null,
    sort:
      sort && (sort.key === "date" || sort.key === "amount") && (sort.dir === "asc" || sort.dir === "desc")
        ? { key: sort.key, dir: sort.dir }
        : null,
  };
}

export function readWealthView(): WealthView {
  if (typeof sessionStorage === "undefined") return { ...EMPTY };
  try {
    return parseWealthView(sessionStorage.getItem(viewKey()));
  } catch {
    return { ...EMPTY };
  }
}

export function patchWealthView(patch: Partial<WealthView>): void {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.setItem(viewKey(), JSON.stringify({ ...readWealthView(), ...patch }));
  } catch {
    /* private mode or a full store: the page works, it only forgets */
  }
}

function inWealth(pathname: string): boolean {
  return pathname === "/wealth" || pathname.startsWith("/wealth/");
}

/**
 * The address to remember for a Wealth page, or null for any other page: the
 * router's location is already the next page's while the one being left is
 * still on screen, so the section is asked about addresses that are not its
 * own. `account` is left out: it is an instruction from the Accounts page
 * ("show this account's rows"), carried out once, and the filter it sets is
 * remembered by itself.
 */
export function wealthTabHref(pathname: string, search: string): string | null {
  const path = pathname.replace(/\/+$/, "") || "/";
  if (!inWealth(path)) return null;
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  params.delete("account");
  const query = params.toString();
  return `${path}${query ? `?${query}` : ""}`;
}

/**
 * Arriving at the section's front page from outside it means "back to Wealth",
 * so it opens the tab that was last open. Inside the section the same address
 * is the Overview tab, and a click on it is a click on Overview.
 *
 * `from` is the page being left (null on a first load), `tabs` the section's
 * tabs: a remembered address that is no longer one of them is not followed.
 */
export function wealthResume(input: { toSearch: string; from: string | null; saved: string | null; tabs: readonly string[] }): string | null {
  const { saved } = input;
  if (!saved || input.toSearch.replace(/^\?/, "") !== "") return null;
  if (input.from != null && inWealth(input.from.replace(/\/+$/, "") || "/")) return null;
  const path = saved.split(/[?#]/)[0]!;
  if (path === "/wealth" || !input.tabs.includes(path)) return null;
  return saved;
}
